type HSL = { h: number; s: number; l: number; a?: number };

export const hexToHSL = (hexColor: string): HSL => {
  let r = '0',
    g = '0',
    b = '0',
    a = 'FF';

  if (hexColor.length === 4) {
    // #RGB
    r = `0x${hexColor[1]}${hexColor[1]}`;
    g = `0x${hexColor[2]}${hexColor[2]}`;
    b = `0x${hexColor[3]}${hexColor[3]}`;
  } else if (hexColor.length === 7) {
    // #RRGGBB
    r = `0x${hexColor[1]}${hexColor[2]}`;
    g = `0x${hexColor[3]}${hexColor[4]}`;
    b = `0x${hexColor[5]}${hexColor[6]}`;
  } else if (hexColor.length === 9) {
    // #RRGGBBAA
    r = `0x${hexColor[1]}${hexColor[2]}`;
    g = `0x${hexColor[3]}${hexColor[4]}`;
    b = `0x${hexColor[5]}${hexColor[6]}`;
    a = `0x${hexColor[7]}${hexColor[8]}`;
  }

  const rDec = parseInt(r, 16) / 255;
  const gDec = parseInt(g, 16) / 255;
  const bDec = parseInt(b, 16) / 255;

  const cMin = Math.min(rDec, gDec, bDec);
  const cMax = Math.max(rDec, gDec, bDec);
  const delta = cMax - cMin;

  let h = 0,
    s = 0;
  const l = (cMax + cMin) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (cMax) {
      case rDec:
        h = ((gDec - bDec) / delta) % 6;
        break;
      case gDec:
        h = (bDec - rDec) / delta + 2;
        break;
      case bDec:
        h = (rDec - gDec) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return {
    h: Math.round(h),
    s: +(s * 100).toFixed(1),
    l: +(l * 100).toFixed(1),
    a: parseInt(a, 16) / 255
  };
};

export const HSLToHex = (
  { h, s, l, a = 1 }: HSL,
  keepAlpha: boolean
): string => {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;
  if (0 <= h && h < 60) {
    r = c;
    g = x;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
  } else if (120 <= h && h < 180) {
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    b = x;
  }

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();

  const rHex = toHex(r);
  const gHex = toHex(g);
  const bHex = toHex(b);

  if (keepAlpha) {
    const aHex = Math.round(a * 255)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
    return `#${rHex}${gHex}${bHex}${aHex}`;
  }

  return `#${rHex}${gHex}${bHex}`;
};
